import {BrandApiDto, CreateUpdateBrandApiDto} from "../../api/model/rest";
import {option, Option} from "ts-option";
import JsonConverter from "../../api/helper/JsonConverter";
import {DealerPrice, VehicleCategory} from "./DealerPrice";

/**
 * Brand model.
 */
export default class Brand {
	constructor(public readonly id: string,
		public readonly name: string,
		public readonly labourCost: number,
		public readonly domain: Option<string>,
		public readonly phoneNumber: string,
		public readonly logoId: Option<string>,
		public readonly siteId: Option<string>,
		public readonly vatRegistrationNumber: Option<string>,
		public readonly freeText: Option<string>,
		public readonly gasNumber: Option<string>,
		public readonly email: Option<string>,
		public readonly vatRegistered: boolean,
		public readonly automaticPaymentLimit: number,
		public readonly customPartsMarkup: number,
		public readonly rewardPointValue: number,
		public readonly vatRate: number,
		public readonly maxFee: number,
		public readonly creditAmounts: DealerPrice[],
		public readonly dealerPrices: DealerPrice[],
		public readonly creditAmountAirToWaterHp: Option<number>,
		public readonly creditAmountAirToAirHp: Option<number>,
		public readonly creditAmountWaterToWaterHp: Option<number>,
		public readonly creditAmountBrineToWaterHp: Option<number>,
		public readonly creditAmountFlowsave: Option<number>,
		public readonly creditValueAirToWaterHp: Option<number>,
		public readonly creditValueAirToAirHp: Option<number>,
		public readonly creditValueWaterToWaterHp: Option<number>,
		public readonly creditValueBrineToWaterHp: Option<number>,
		public readonly creditValueFlowsave: Option<number>,
		/**
		 * The most a single mileage claim may pay out, in cents. Inherited by every partner of this brand
		 * that has not been given a departure from it. Always set - the backend backfilled every brand.
		 */
		public readonly creditValueCap: number) {}

	static fromJson(json: BrandApiDto): Brand {
		return new Brand(json.id, json.name, json.labourCost, option(json.domain),
			json.phoneNumber, option(json.logoId), option(json.siteId), option(json.vatRegistrationNumber), option(json.freeText),
			option(json.gasNumber), option(json.email), json.vatRegistered, json.automaticPaymentLimit, json.customPartsMarkup,
			json.rewardPointValue, json.vatRate, json.maxFee,
			(json.creditAmounts || []).map(p => ({
				vehicleCategory: p.vehicleCategory as VehicleCategory,
				registrationYear: p.registrationYear !== undefined ? p.registrationYear : null,
				pricePennies: p.pricePennies
			})),
			(json.dealerPrices || []).map(p => ({
				vehicleCategory: p.vehicleCategory as VehicleCategory,
				registrationYear: p.registrationYear !== undefined ? p.registrationYear : null,
				pricePennies: p.pricePennies
			})),
			option(json.creditAmountAirToWaterHp), option(json.creditAmountAirToAirHp), option(json.creditAmountWaterToWaterHp),
			option(json.creditAmountBrineToWaterHp), option(json.creditAmountFlowsave),
			option(json.creditValueAirToWaterHp), option(json.creditValueAirToAirHp), option(json.creditValueWaterToWaterHp),
			option(json.creditValueBrineToWaterHp), option(json.creditValueFlowsave), json.creditValueCap);
	}

	toJson(): CreateUpdateBrandApiDto {
		const json: CreateUpdateBrandApiDto = {
			name: this.name,
			labourCost: this.labourCost,
			domain: this.domain.orNull,
			phoneNumber: this.phoneNumber,
			logoId: this.logoId.orNull,
			siteId: this.siteId.orNull,
			vatRegistrationNumber: this.vatRegistrationNumber.orNull,
			freeText: this.freeText.orNull,
			gasNumber: this.gasNumber.orNull,
			email: this.email.orNull,
			vatRegistered: this.vatRegistered,
			automaticPaymentLimit: this.automaticPaymentLimit,
			customPartsMarkup: this.customPartsMarkup,
			rewardPointValue: this.rewardPointValue,
			vatRate: this.vatRate,
			maxFee: this.maxFee,
			creditAmounts: this.creditAmounts.map(p => ({
				vehicleCategory: p.vehicleCategory,
				registrationYear: p.registrationYear !== null ? p.registrationYear : undefined,
				pricePennies: p.pricePennies
			})),
			dealerPrices: this.dealerPrices.map(p => ({
				vehicleCategory: p.vehicleCategory,
				registrationYear: p.registrationYear !== null ? p.registrationYear : undefined,
				pricePennies: p.pricePennies
			})),
			creditAmountAirToWaterHp: this.creditAmountAirToWaterHp.orNull,
			creditAmountAirToAirHp: this.creditAmountAirToAirHp.orNull,
			creditAmountWaterToWaterHp: this.creditAmountWaterToWaterHp.orNull,
			creditAmountBrineToWaterHp: this.creditAmountBrineToWaterHp.orNull,
			creditAmountFlowsave: this.creditAmountFlowsave.orNull,
			creditValueAirToWaterHp: this.creditValueAirToWaterHp.orNull,
			creditValueAirToAirHp: this.creditValueAirToAirHp.orNull,
			creditValueWaterToWaterHp: this.creditValueWaterToWaterHp.orNull,
			creditValueBrineToWaterHp: this.creditValueBrineToWaterHp.orNull,
			creditValueFlowsave: this.creditValueFlowsave.orNull,
			creditValueCap: this.creditValueCap
		};
		return JsonConverter.removeEmptyFields(json);
	}

	copy({
		id = this.id,
		name = this.name,
		labourCost = this.labourCost,
		domain = this.domain,
		phoneNumber = this.phoneNumber,
		logoId = this.logoId,
		siteId = this.siteId,
		vatRegistrationNumber = this.vatRegistrationNumber,
		freeText = this.freeText,
		gasNumber = this.gasNumber,
		email = this.email,
		vatRegistered = this.vatRegistered,
		automaticPaymentLimit = this.automaticPaymentLimit,
		customPartsMarkup = this.customPartsMarkup,
		rewardPointValue = this.rewardPointValue,
		vatRate = this.vatRate,
		maxFee = this.maxFee,
		creditAmounts = this.creditAmounts,
		dealerPrices = this.dealerPrices,
		creditAmountAirToWaterHp = this.creditAmountAirToWaterHp,
		creditAmountAirToAirHp = this.creditAmountAirToAirHp,
		creditAmountWaterToWaterHp = this.creditAmountWaterToWaterHp,
		creditAmountBrineToWaterHp = this.creditAmountBrineToWaterHp,
		creditAmountFlowsave = this.creditAmountFlowsave,
		creditValueAirToWaterHp = this.creditValueAirToWaterHp,
		creditValueAirToAirHp = this.creditValueAirToAirHp,
		creditValueWaterToWaterHp = this.creditValueWaterToWaterHp,
		creditValueBrineToWaterHp = this.creditValueBrineToWaterHp,
		creditValueFlowsave = this.creditValueFlowsave,
		creditValueCap = this.creditValueCap
	}) {
		return new Brand(id, name, labourCost, domain, phoneNumber, logoId, siteId,
			vatRegistrationNumber, freeText, gasNumber, email, vatRegistered, automaticPaymentLimit, customPartsMarkup,
			rewardPointValue, vatRate, maxFee, creditAmounts, dealerPrices,
			creditAmountAirToWaterHp, creditAmountAirToAirHp, creditAmountWaterToWaterHp, creditAmountBrineToWaterHp,
			creditAmountFlowsave, creditValueAirToWaterHp, creditValueAirToAirHp, creditValueWaterToWaterHp,
			creditValueBrineToWaterHp, creditValueFlowsave, creditValueCap);
	}
}
